import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import ClinicIcon from "./ClinicIcon";
import "./Toast.css";

// --- Types ---
export type ToastType = "success" | "error" | "info" | "warning";

export interface ShowToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
}

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (opts: ShowToastOptions) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_LIMIT = 3;

const getToastIconName = (type: ToastType) => {
  switch (type) {
    case "success": return "check";
    case "error": return "close";
    case "warning": return "report";
    case "info":
    default: return "bell";
  }
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ message, type = "info", duration = 4000 }: ShowToastOptions) => {
      const id = Math.random().toString(36).substring(2, 9);

      setToasts((current) => {
        const updated = [...current, { id, message, type }];
        return updated.slice(-TOAST_LIMIT);
      });

      if (duration > 0) {
        window.setTimeout(() => {
          setToasts((current) => current.filter((t) => t.id !== id));
        }, duration);
      }
    },
    []
  );

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}

      <div className="toast-viewport">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast-${t.type}`}
            role="alert"
          >
            <div className="toast-icon-wrapper">
              <ClinicIcon name={getToastIconName(t.type) as any} size={20} />
            </div>
            
            <span className="toast-message">{t.message}</span>

            <button
              className="toast-close"
              onClick={() => removeToast(t.id)}
              aria-label="Close"
            >
              <ClinicIcon name="close" size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}